import { MouseEventHandler, useContext, useMemo } from 'react';
import { IconButton } from 'src/components/Buttons';
import { useAdCampaignsDataContext } from 'src/contexts/AdCampaignsDataContext';
import { Content } from 'src/contexts/ContentContext';
import {
  getContent,
  useContentContext,
  useFormattedContent,
} from 'src/contexts/ContentContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { PosProgressBar } from 'src/core/POS/PosProgressBar/PosProgressBar';
import { vars } from 'src/core/themes';
import { ColorsV2 } from 'src/core/themes/constants/colors';
import { Button, Stack } from 'src/core/ui';
import { CampaignModal } from 'src/modals/SponsoredListings/CampaignModal';
import { OpenLinkIcon } from 'src/svgs';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { formatCurrency } from 'src/utils/numberFormatter';
import {
  AdCampaignState,
  FlightDuration,
  SponsoredListingsCampaign,
} from 'src/WebApiController';

import { useBoostIcon } from '../useBoostIcon';

export type BoostListingButtonProps = {
  campaign?: SponsoredListingsCampaign | null;
  eventId?: number | null;
  shape?: 'pill' | 'rect';
  height?: number | string;
  showIcon?: boolean;
  iconOnly?: boolean;
  backgroundIsPrimary?: boolean;
  disabled?: boolean;
};
export const BoostListingButton = ({
  campaign,
  eventId,
  height,
  showIcon,
  iconOnly,
  backgroundIsPrimary,
  disabled,
}: BoostListingButtonProps) => {
  const { setModal } = useContext(ModalContext);
  const { allCampaignData, isEventsEnabledForSponsoredListingsLoading } =
    useAdCampaignsDataContext();

  const campaignForEvent = useMemo(() => {
    if (campaign) return campaign;

    if (eventId == null || !allCampaignData) {
      return null;
    }

    const activeCampaigns = (allCampaignData[eventId] ?? []).filter(
      (c) => c.campaignState == AdCampaignState.Active
    );

    // First try to find a campaign with activeFlight
    const campaignWithActiveFlight = activeCampaigns.find(
      (c) => c.activeFlight
    );

    if (campaignWithActiveFlight) {
      return campaignWithActiveFlight;
    }

    // Fall back to any active campaign
    return activeCampaigns[0] ?? null;
  }, [allCampaignData, campaign, eventId]);

  const onClick: MouseEventHandler = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!eventId && !campaignForEvent?.eventId) {
      return;
    }

    setModal({
      children: (
        <CampaignModal
          eventId={eventId ?? campaignForEvent!.eventId!}
          campaign={campaignForEvent}
        />
      ),
      clickOutsideToClose: true,
      size: 'slide-in-md',
    });
  };

  const isEnabled = useMemo(
    () =>
      campaignForEvent ||
      isEventsEnabledForSponsoredListingsLoading(eventId ?? null),
    [campaignForEvent, eventId, isEventsEnabledForSponsoredListingsLoading]
  );

  const BoostIconDisplay = useBoostIcon();

  const contentContext = useContentContext();

  const spendOverBudgetText = useFormattedContent(
    FormatContentId.ValueOfValue,
    [
      formatCurrency(
        (campaignForEvent?.activeFlight?.flightBudget ?? 0) -
          (campaignForEvent?.activeFlight?.flightSpend ?? 0),
        campaignForEvent?.currencyCode ?? 'USD'
      ),
      formatCurrency(
        campaignForEvent?.activeFlight?.flightBudget,
        campaignForEvent?.currencyCode ?? 'USD'
      ),
    ]
  ) as string;

  const flightDurationText = useMemo(() => {
    const flightDuration = campaignForEvent?.flightDuration;
    switch (flightDuration) {
      case FlightDuration.Daily:
        return getContent(ContentId.Daily, contentContext).toLocaleLowerCase();
      case FlightDuration.Weekly:
        return getContent(
          ContentId.ThisWeek,
          contentContext
        ).toLocaleLowerCase();
      case FlightDuration.Monthly:
        return getContent(
          ContentId.ThisMonth,
          contentContext
        ).toLocaleLowerCase();
      default:
        return getContent(
          ContentId.TheCurrentFlight,
          contentContext
        ).toLocaleLowerCase();
    }
  }, [campaignForEvent?.flightDuration, contentContext]);

  const spendRemainingBudgetText = useFormattedContent(
    FormatContentId.RemainingBudget,
    [flightDurationText]
  ) as string;

  if (!isEnabled) {
    return null;
  }

  return iconOnly ? (
    <IconButton
      disabled={disabled}
      icon={
        <OpenLinkIcon size={vars.iconSize.m} fill={vars.color.textBrandV2} />
      }
      onClick={onClick}
    />
  ) : (
    <Button
      disabled={disabled}
      variant="outlineBrand"
      shape="rect"
      style={{
        height,
        paddingLeft: vars.spacing.sm,
        paddingRight: vars.spacing.sm,
        borderColor:
          campaignForEvent &&
          campaignForEvent.campaignState !== AdCampaignState.Active
            ? ColorsV2.error020
            : undefined,
      }}
      color={vars.color.textBrandV2}
      onClick={onClick}
    >
      {campaignForEvent && (
        <Stack
          gap="m"
          alignItems="center"
          style={{ flex: 1 }}
          title={`${spendOverBudgetText} ${spendRemainingBudgetText.toLocaleLowerCase()}`}
        >
          <PosProgressBar
            style={{
              width: '80px',
              backgroundColor: backgroundIsPrimary
                ? vars.color.surfaceSecondary
                : vars.color.surfacePrimary,
            }}
            color={
              campaignForEvent?.campaignState !== AdCampaignState.Active
                ? ColorsV2.error020
                : vars.color.textBrandV2
            }
            value={
              (((campaignForEvent.activeFlight?.flightBudget ?? 0) -
                (campaignForEvent.activeFlight?.flightSpend ?? 0)) /
                (campaignForEvent.activeFlight?.flightBudget ?? 1)) *
              100
            }
          />
          <span
            style={{
              color:
                campaignForEvent.campaignState !== AdCampaignState.Active
                  ? vars.color.textErrorV2
                  : vars.color.textBrandV2,
            }}
          >
            {formatCurrency(
              (campaignForEvent.activeFlight?.flightBudget ?? 0) -
                (campaignForEvent.activeFlight?.flightSpend ?? 0),
              campaignForEvent.currencyCode ?? 'USD'
            )}
          </span>
        </Stack>
      )}
      {(showIcon || campaignForEvent) && (
        <BoostIconDisplay
          size={vars.iconSize.m}
          inactive={campaignForEvent?.campaignState !== AdCampaignState.Active}
        />
      )}
      {!campaignForEvent && (
        <span style={{ whiteSpace: 'nowrap' }}>
          <Content id={ContentId.BoostEvent} />
        </span>
      )}
    </Button>
  );
};
